import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { FontType, defaultFont } from '../redux/cardAttributeModelIndex';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type UiCardItemType = 'input' | 'text' | 'button' | 'checkbox' | 'dropdown' | 'multi' | 'variable' | 'static' | 'group';

type UiCardItemBase = {
  cardId: string;
  parentId: string;
  name: string;
  x: number;
  y: number;
  z: number;
  width: number;
  height: number;
  notes: string;
}
type ChoicesHashType = {
  [key in string]: string;
}

export type InputUiSketchItem = UiCardItemBase & {
  type: 'input';
  label: string;
  labelVisibility: boolean;
  labelFont: FontType;
  placeholder: string;
};
export type TextUiSketchItem = UiCardItemBase & {
  type: 'text';
  text: string;
  textFont: FontType;
  label: string;
  labelVisibility: boolean;
  labelFont: FontType;
  flame: boolean;
  flameColor: string;
  backgroundColor: string;
  backgroundOpacity: number;
};
export type ButtonUiSketchItem = UiCardItemBase & {
  type: 'button';
  label: string;
  labelFont: FontType;
  backgroundColor: string;
};
export type CheckboxUiSketchItem = UiCardItemBase & {
  type: 'checkbox';
  label: string;
  labelVisibility: boolean;
  labelFont: FontType;
};
export type DropdownUiSketchItem = UiCardItemBase & {
  type: 'dropdown';
  label: string;
  labelVisibility: boolean;
  labelFont: FontType;
  options: ChoicesHashType;
};
export type MultiInputUiSketchItem = UiCardItemBase & {
  type: 'multi';
  label: string;
  labelVisibility: boolean;
  labelFont: FontType;
  placeholder: string;
};
export type VariableImageUiSketchItem = UiCardItemBase & {
  type: 'variable';
  button: boolean;
  radius: number;
};
export type StaticImageUiSketchItem = UiCardItemBase & {
  type: 'static';
  imageId: string;
  button: boolean;
  radius: number;
};
export type GroupUiSketchItem = UiCardItemBase & {
  type: 'group';
};

export type UiCardItemDb = InputUiSketchItem | TextUiSketchItem | ButtonUiSketchItem |
  CheckboxUiSketchItem | DropdownUiSketchItem | MultiInputUiSketchItem |
  VariableImageUiSketchItem | StaticImageUiSketchItem | GroupUiSketchItem;

const initialBase: UiCardItemBase = {
  cardId: '',
  parentId: '',
  name: '',
  x: 0,
  y: 0,
  z: 0,
  width: 0,
  height: 0,
  notes: '',
}
type UiCardItemInitialType = UiCardItemDb[]
const initial: UiCardItemInitialType = [
  {
    ...initialBase,
    type: 'input',
    label: '',
    labelVisibility: true,
    labelFont: defaultFont,
    placeholder: '',
  },
  {
    ...initialBase,
    type: 'text',
    text: '',
    textFont: defaultFont,
    label: '',
    labelVisibility: true,
    labelFont: defaultFont,
    flame: true,
    flameColor: '',
    backgroundColor: '',
    backgroundOpacity: 0
  },
  {
    ...initialBase,
    type: 'button',
    label: '',
    labelFont: defaultFont,
    backgroundColor: ''
  },
  {
    ...initialBase,
    type: 'checkbox',
    label: '',
    labelVisibility: true,
    labelFont: defaultFont
  },
  {
    ...initialBase,
    type: 'dropdown',
    label: '',
    labelVisibility: true,
    labelFont: defaultFont,
    options: {}
  },
  {
    ...initialBase,
    type: 'multi',
    label: '',
    labelVisibility: true,
    labelFont: defaultFont,
    placeholder: ''
  },
  {
    ...initialBase,
    type: 'variable',
    button: true,
    radius: 0
  },
  {
    ...initialBase,
    type: 'static',
    imageId: '',
    button: true,
    radius: 0
  },
  {
    ...initialBase,
    type: 'group'
  },
]

export type UiCardItemDbHashType = {
  [key in string]: UiCardItemDb;
}

export type UiCardItemAddPayload = {
  id?: string;
} & UiCardItemDb;

export type UiCardItemUpdatePayload = {
  id: string;
} & {[P in keyof UiCardItemDb]?: UiCardItemDb[P]};

export type UiCardItemDbState = {
  uiCardItems: UiCardItemDbHashType;
}

const isUiCardItemDb = (obj: any): obj is UiCardItemDb => {
  return 'type' in obj;
}
const isSameType = <T, _>(a: T, b: any): b is T => {
  return isUiCardItemDb(a) && a.type === b?.type;
}
export const isStaticImageUiSketchItem = (obj: any): obj is StaticImageUiSketchItem => {
  return isUiCardItemDb(obj) && obj.type === 'static'
}

export const generateUiCardItemId = (): string => generateId('uci');
let initialState: UiCardItemDbState = { uiCardItems: {} };
const key = 'uiCardItems';

const uiCardItemDbSlice = createSlice({
  name: 'uiCardItemDb',
  initialState,
  reducers: {
    initializeUiCardItems(state: UiCardItemDbState) {
      state.uiCardItems = {};
    },
    addUiCardItem(state: UiCardItemDbState, action: PayloadAction<UiCardItemAddPayload>) {
      let {id, ...obj} = action.payload;
      if (!id) id = generateUiCardItemId();
      state.uiCardItems[id] = obj;
      saveData(key, id, obj);
    },
    updateUiCardItem(state: UiCardItemDbState, action: PayloadAction<UiCardItemUpdatePayload>) {
      const {id, ...obj} = action.payload;
      const uiCardItem = state.uiCardItems[id];
      if (uiCardItem === undefined) {
        console.log(`*** UiCardItem not found: ${id}`);
      } else if (isSameType(state.uiCardItems[id], obj)) {
        const newData = { ...state.uiCardItems[id], ...obj };
        state.uiCardItems[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteUiCardItem(state: UiCardItemDbState, action: PayloadAction<UiCardItemUpdatePayload>) {
      const id = action.payload.id;
      delete state.uiCardItems[id];
      deleteData(key, id);
    },
    saveUiCardItems(state: UiCardItemDbState, action: PayloadAction<void>) {
      saveAllData(key, state.uiCardItems);
    },
    signUpUiCardItems(state: UiCardItemDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.uiCardItems, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: UiCardItemDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.uiCardItems = updatedValues;
      if (changed) saveAllData(key, state.uiCardItems);
    })
  }
})

export const { initializeUiCardItems, addUiCardItem, updateUiCardItem, deleteUiCardItem, saveUiCardItems, signUpUiCardItems } = uiCardItemDbSlice.actions;
export default uiCardItemDbSlice.reducer;
